import Phaser from "phaser";
import {
  createPolarityAdminApi,
  type PolarityAdminResult,
  type PolarityAdminState,
} from "./admin";
import {
  ARENA_BOUNDS,
  COLORS,
  GAME_HEIGHT,
  GAME_WIDTH,
  PHYSICS,
  chargeToFillColor,
  chargeToHighlightColor,
  wallColorToFillColor,
} from "./config";
import type { ArenaBounds } from "./config";
import {
  type MobileUiController,
  type ViewportMode,
} from "./mobileUi";
import { PERK_DEFINITIONS, type PerkId } from "./perks";
import {
  createArenaProjection,
  screenLengthToWorld,
  screenToWorldPoint,
  worldLengthToScreen,
  worldToScreenPoint,
  type ArenaProjection,
} from "./projection";
import { PolaritySimulation, type BallSnapshot, type SimulationSnapshot } from "./simulation";
import { tracePolarity } from "./trace";

interface GameSceneOptions {
  mobileUi?: MobileUiController;
}

const MOBILE_TAP_SLOP = 18;
const TIMER_BAR_HEIGHT = 24;
const TERMINAL_BUTTON_WIDTH = 140;
const TERMINAL_BUTTON_HEIGHT = 52;
const PAUSE_BUTTON_ICON_COLOR = 0xeef7ff;
const SHOP_PANEL_ALPHA = 0.94;
const PERK_BUTTON_WIDTH_MULTIPLIER = 3;

interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface SceneLayout {
  width: number;
  height: number;
  availableArena: ArenaBounds;
  arena: ArenaBounds;
  projection: ArenaProjection;
  safeArea: SafeAreaInsets;
  scoreTextX: number;
  scoreTextY: number;
  levelTextX: number;
  levelTextY: number;
  timerTextX: number;
  timerTextY: number;
  pauseButtonX: number;
  pauseButtonY: number;
  pauseButtonSize: number;
  pauseButtonHitSize: number;
  shopX: number;
  shopY: number;
  shopWidth: number;
  shopRowHeight: number;
  shopTitleHeight: number;
  shopFontSize: string;
  shopTitleFontSize: string;
  perkButtonX: number;
  perkButtonStartY: number;
  perkButtonWidth: number;
  perkButtonSize: number;
  perkButtonGap: number;
  perkButtonFontSize: string;
  perkButtonCountFontSize: string;
  statusTextX: number;
  statusTextY: number;
  hintTextX: number;
  hintTextY: number;
  bannerTextX: number;
  pauseOverlayX: number;
  bannerTextY: number;
  bannerSubTextY: number;
  timerBarY: number;
  timerBarHeight: number;
  terminalButtonX: number;
  terminalButtonY: number;
  fontSizes: {
    hud: string;
    status: string;
    hint: string;
    banner: string;
    bannerSub: string;
  };
}

export class GameScene extends Phaser.Scene {
  private readonly mobileUi?: MobileUiController;
  private simulation!: PolaritySimulation;
  private graphics!: Phaser.GameObjects.Graphics;
  private scoreText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private bannerText!: Phaser.GameObjects.Text;
  private bannerSubText!: Phaser.GameObjects.Text;
  private terminalButtonBackground!: Phaser.GameObjects.Graphics;
  private terminalButtonText!: Phaser.GameObjects.Text;
  private terminalButtonZone!: Phaser.GameObjects.Zone;
  private pauseButtonZone!: Phaser.GameObjects.Zone;
  private shopTitleText!: Phaser.GameObjects.Text;
  private shopItemTexts: Phaser.GameObjects.Text[] = [];
  private shopItemZones: Phaser.GameObjects.Zone[] = [];
  private perkButtonTexts: Phaser.GameObjects.Text[] = [];
  private perkButtonCountTexts: Phaser.GameObjects.Text[] = [];
  private perkButtonZones: Phaser.GameObjects.Zone[] = [];
  private accumulatorMs = 0;
  private manualAdvance = false;
  private adminEnabled = false;
  private pendingFlipIndices: number[] = [];
  private updateBatchCount = 0;
  private viewportMode: ViewportMode = "desktop";
  private layout!: SceneLayout;
  private removeViewportModeListener?: () => void;

  constructor(options: GameSceneOptions = {}) {
    super("polarity-scene");
    this.mobileUi = options.mobileUi;
  }

  create(): void {
    this.adminEnabled = window.__polarityAdminEnabled === true;
    const adminEnabled = this.adminEnabled;
    const debugHooksEnabled = window.__polarityDebugHooksEnabled === true;
    this.viewportMode = this.mobileUi?.getViewportMode() ?? "desktop";
    this.layout = this.computeLayout();
    this.simulation = new PolaritySimulation(1);
    this.manualAdvance = debugHooksEnabled && typeof window.advanceTime === "function";
    tracePolarity("scene_created", {
      adminEnabled,
      debugHooksEnabled,
      manualAdvance: this.manualAdvance,
      viewportMode: this.viewportMode,
    });
    this.graphics = this.add.graphics();

    this.scoreText = this.add.text(this.layout.scoreTextX, this.layout.scoreTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.hud,
      color: COLORS.textPrimary,
      stroke: "#041018",
      strokeThickness: 3,
    });
    this.scoreText.setOrigin(0, 0.5);

    this.levelText = this.add.text(this.layout.levelTextX, this.layout.levelTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.hud,
      color: COLORS.textPrimary,
      stroke: "#041018",
      strokeThickness: 3,
    });
    this.levelText.setOrigin(0.5, 0.5);

    this.timerText = this.add.text(this.layout.timerTextX, this.layout.timerTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.hud,
      color: COLORS.textPrimary,
      stroke: "#041018",
      strokeThickness: 3,
    });
    this.timerText.setOrigin(1, 0.5);

    this.statusText = this.add.text(this.layout.statusTextX, this.layout.statusTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.status,
      color: COLORS.warning,
      align: "center",
    });
    this.statusText.setOrigin(0.5, 0);

    this.hintText = this.add.text(this.layout.hintTextX, this.layout.hintTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.hint,
      color: COLORS.textMuted,
      align: "center",
    });
    this.hintText.setOrigin(0.5, 0.5);

    this.bannerText = this.add.text(this.layout.bannerTextX, this.layout.bannerTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.banner,
      color: COLORS.textPrimary,
      align: "center",
      stroke: "#041018",
      strokeThickness: 4,
    });
    this.bannerText.setOrigin(0.5, 0.5);

    this.bannerSubText = this.add.text(this.layout.bannerTextX, this.layout.bannerSubTextY, "", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.fontSizes.bannerSub,
      color: COLORS.textPrimary,
      align: "center",
    });
    this.bannerSubText.setOrigin(0.5, 0.5);

    this.terminalButtonBackground = this.add.graphics();
    this.terminalButtonText = this.add.text(
      this.layout.terminalButtonX,
      this.layout.terminalButtonY,
      "",
      {
        fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
        fontSize: "20px",
        color: COLORS.textPrimary,
        fontStyle: "bold",
        align: "center",
      },
    );
    this.terminalButtonText.setOrigin(0.5, 0.5);
    this.terminalButtonZone = this.add.zone(
      this.layout.terminalButtonX,
      this.layout.terminalButtonY,
      TERMINAL_BUTTON_WIDTH,
      TERMINAL_BUTTON_HEIGHT,
    );
    this.terminalButtonZone.setInteractive({ useHandCursor: true });
    this.terminalButtonZone.on(Phaser.Input.Events.POINTER_DOWN, () => {
      this.handleTerminalButton();
    });
    this.pauseButtonZone = this.add.zone(
      this.layout.pauseButtonX,
      this.layout.pauseButtonY,
      this.layout.pauseButtonHitSize,
      this.layout.pauseButtonHitSize,
    );
    this.pauseButtonZone.setInteractive({ useHandCursor: true });
    this.pauseButtonZone.on(Phaser.Input.Events.POINTER_DOWN, () => {
      this.handlePauseButton();
    });

    this.shopTitleText = this.add.text(0, 0, "Perk Shop", {
      fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
      fontSize: this.layout.shopTitleFontSize,
      color: COLORS.textPrimary,
      fontStyle: "bold",
      stroke: "#041018",
      strokeThickness: 2,
    });
    this.shopTitleText.setOrigin(0, 0.5);

    for (const perk of PERK_DEFINITIONS) {
      const itemText = this.add.text(0, 0, "", {
        fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
        fontSize: this.layout.shopFontSize,
        color: COLORS.textPrimary,
        stroke: "#041018",
        strokeThickness: 2,
      });
      itemText.setOrigin(0, 0.5);
      this.shopItemTexts.push(itemText);

      const itemZone = this.add.zone(0, 0, 1, 1);
      itemZone.setInteractive({ useHandCursor: true });
      itemZone.on(Phaser.Input.Events.POINTER_DOWN, () => {
        this.handleShopItem(perk.id);
      });
      this.shopItemZones.push(itemZone);

      const buttonText = this.add.text(0, 0, perk.sign, {
        fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
        fontSize: this.layout.perkButtonFontSize,
        color: COLORS.textPrimary,
        fontStyle: "bold",
        align: "center",
        stroke: "#041018",
        strokeThickness: 2,
      });
      buttonText.setOrigin(0.5, 0.5);
      this.perkButtonTexts.push(buttonText);

      const countText = this.add.text(0, 0, "", {
        fontFamily: "\"Trebuchet MS\", \"Avenir Next\", sans-serif",
        fontSize: this.layout.perkButtonCountFontSize,
        color: COLORS.textPrimary,
        fontStyle: "bold",
        align: "center",
        stroke: "#041018",
        strokeThickness: 2,
      });
      countText.setOrigin(0.5, 0.5);
      this.perkButtonCountTexts.push(countText);

      const buttonZone = this.add.zone(0, 0, 1, 1);
      buttonZone.setInteractive({ useHandCursor: true });
      buttonZone.on(Phaser.Input.Events.POINTER_DOWN, () => {
        this.handlePerkButton(perk.id);
      });
      this.perkButtonZones.push(buttonZone);
    }
    this.applyLayoutToSceneObjects();

    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (this.viewportMode === "mobilePortrait") {
        return;
      }

      const snapshot = this.simulation.getSnapshot();
      if (this.isPointerOnUi(pointer.x, pointer.y, snapshot)) {
        return;
      }

      if (snapshot.status !== "running") {
        return;
      }

      const worldPoint = screenToWorldPoint(this.layout.projection, {
        x: pointer.x,
        y: pointer.y,
      });
      const hitSlopWorld =
        this.viewportMode === "desktop"
          ? 0
          : screenLengthToWorld(this.layout.projection, MOBILE_TAP_SLOP);
      const ballIndex = this.simulation.findBallIndexAt(
        worldPoint.x,
        worldPoint.y,
        hitSlopWorld,
      );
      if (ballIndex !== null) {
        const ball = snapshot.balls[ballIndex];
        tracePolarity("pointer_down_queued", {
          updateBatch: this.updateBatchCount,
          pointerX: round(pointer.x),
          pointerY: round(pointer.y),
          worldX: round(worldPoint.x),
          worldY: round(worldPoint.y),
          ballIndex,
          ballId: ball?.id ?? null,
          ballCharge: ball?.charge ?? null,
          ballX: ball?.x ?? null,
          ballY: ball?.y ?? null,
          status: snapshot.status,
          pendingBefore: this.pendingFlipIndices.length,
        });
        this.pendingFlipIndices.push(ballIndex);
      } else {
        tracePolarity("pointer_down_empty", {
          updateBatch: this.updateBatchCount,
          pointerX: round(pointer.x),
          pointerY: round(pointer.y),
          worldX: round(worldPoint.x),
          worldY: round(worldPoint.y),
          status: snapshot.status,
          pendingBefore: this.pendingFlipIndices.length,
        });
      }
    });

    this.input.keyboard?.on("keydown-R", () => {
      this.restartLevel("keyboard");
    });

    const confirm = () => {
      this.confirmProgress("keyboard");
    };

    this.input.keyboard?.on("keydown-ENTER", confirm);
    this.input.keyboard?.on("keydown-SPACE", (event: KeyboardEvent) => {
      this.handleSpaceKey(event);
    });
    this.input.keyboard?.on("keydown-P", () => {
      this.togglePause("keyboard");
    });
    this.input.keyboard?.on("keydown-ESC", () => {
      this.togglePause("keyboard");
    });
    this.input.keyboard?.on("keydown-S", (event: KeyboardEvent) => {
      if (!adminEnabled || !event.ctrlKey) {
        return;
      }

      event.preventDefault();
      this.skipLevel("hotkey");
    });
    this.input.keyboard?.on("keydown-A", (event: KeyboardEvent) => {
      if (!adminEnabled || !event.ctrlKey) {
        return;
      }

      event.preventDefault();
      this.previousLevel("hotkey");
    });

    if (adminEnabled) {
      window.polarityAdmin = createPolarityAdminApi({
        getState: () => this.getAdminState(),
        forcePassLevel: () => this.forcePassLevel(),
        skipLevel: () => this.skipLevel("api"),
        previousLevel: () => this.previousLevel("api"),
        jumpToLevel: (level) => this.jumpToLevel(level),
        addScore: (amount) => this.addScore(amount),
      });
    } else {
      window.polarityAdmin = undefined;
    }

    this.removeViewportModeListener = this.mobileUi?.subscribeViewportMode((mode) => {
      this.viewportMode = mode;
      this.pendingFlipIndices = [];
      this.syncResponsiveLayout();
      this.renderFrame();
    });
    this.scale.on(Phaser.Scale.Events.RESIZE, this.handleScaleResize);

    if (debugHooksEnabled) {
      window.render_game_to_text = () => this.simulation.renderToText();
      window.advanceTime = async (ms: number) => {
        this.manualAdvance = true;
        this.advanceSimulation(ms, false);
      };
    } else {
      window.render_game_to_text = undefined;
      window.advanceTime = undefined;
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.removeViewportModeListener?.();
      this.scale.off(Phaser.Scale.Events.RESIZE, this.handleScaleResize);
      window.polarityAdmin = undefined;
      window.render_game_to_text = undefined;
      window.advanceTime = undefined;
    });

    this.renderFrame();
  }

  update(_time: number, delta: number): void {
    if (this.viewportMode === "mobilePortrait") {
      return;
    }

    if (!this.manualAdvance) {
      this.advanceSimulation(delta);
    }
  }

  private advanceSimulation(deltaMs: number, clampFrameDelta = true): void {
    if (this.viewportMode === "mobilePortrait") {
      this.renderFrame();
      return;
    }

    this.updateBatchCount += 1;
    const updateBatch = this.updateBatchCount;
    const statusBefore = this.simulation.getSnapshot().status;
    const pendingBefore = this.pendingFlipIndices.length;
    const elapsedMs = Math.max(0, clampFrameDelta ? Math.min(deltaMs, 100) : deltaMs);
    this.accumulatorMs += elapsedMs;
    let advancedAtLeastOneStep = false;
    let stepsRun = 0;

    while (this.accumulatorMs >= PHYSICS.fixedStepMs) {
      this.simulation.step(PHYSICS.fixedStepMs);
      this.accumulatorMs -= PHYSICS.fixedStepMs;
      advancedAtLeastOneStep = true;
      stepsRun += 1;
    }

    if (advancedAtLeastOneStep) {
      this.flushPendingFlips(updateBatch, stepsRun);
    }

    const statusAfter = this.simulation.getSnapshot().status;
    if (pendingBefore > 0 || statusBefore !== statusAfter) {
      tracePolarity("scene_update_summary", {
        updateBatch,
        deltaMs: round(deltaMs),
        stepsRun,
        pendingBefore,
        pendingAfter: this.pendingFlipIndices.length,
        statusBefore,
        statusAfter,
      });
    }

    this.renderFrame();
  }

  private flushPendingFlips(updateBatch: number, stepsRun: number): void {
    if (this.pendingFlipIndices.length === 0) {
      return;
    }

    const pending = this.pendingFlipIndices;
    this.pendingFlipIndices = [];
    const beforeSnapshot = this.simulation.getSnapshot();
    tracePolarity("queue_flush_started", {
      updateBatch,
      stepsRun,
      pendingIndices: [...pending],
      statusBefore: beforeSnapshot.status,
    });

    for (const ballIndex of pending) {
      const beforeBall = beforeSnapshot.balls[ballIndex];
      const flipApplied = this.simulation.flipBall(ballIndex);
      const afterSnapshot = this.simulation.getSnapshot();
      const afterBall = afterSnapshot.balls[ballIndex];
      tracePolarity("queue_flush_result", {
        updateBatch,
        stepsRun,
        ballIndex,
        ballId: beforeBall?.id ?? null,
        chargeBefore: beforeBall?.charge ?? null,
        chargeAfter: afterBall?.charge ?? null,
        flipApplied,
        statusAfter: afterSnapshot.status,
      });
    }
  }

  private readonly handleScaleResize = (): void => {
    this.syncResponsiveLayout();
    this.renderFrame();
  };

  private shouldShowPerkShop(snapshot: SimulationSnapshot): boolean {
    return (
      this.viewportMode !== "mobilePortrait" &&
      (snapshot.status === "paused" || snapshot.status === "won" || snapshot.status === "lost")
    );
  }

  private isPointerOnUi(x: number, y: number, snapshot: SimulationSnapshot): boolean {
    const layout = this.layout;

    if (
      (snapshot.status === "running" || snapshot.status === "paused") &&
      pointInCenteredRect(
        x,
        y,
        layout.pauseButtonX,
        layout.pauseButtonY,
        layout.pauseButtonHitSize,
        layout.pauseButtonHitSize,
      )
    ) {
      return true;
    }

    if (
      (snapshot.status === "paused" || snapshot.status === "won" || snapshot.status === "lost") &&
      pointInCenteredRect(
        x,
        y,
        snapshot.status === "paused" ? layout.pauseOverlayX : layout.terminalButtonX,
        layout.terminalButtonY,
        TERMINAL_BUTTON_WIDTH,
        TERMINAL_BUTTON_HEIGHT,
      )
    ) {
      return true;
    }

    if (this.shouldShowPerkShop(snapshot)) {
      const shopHeight = layout.shopTitleHeight + snapshot.perks.shop.length * layout.shopRowHeight;
      if (pointInRect(x, y, layout.shopX, layout.shopY, layout.shopWidth, shopHeight)) {
        return true;
      }
    }

    if (snapshot.status !== "running" && snapshot.status !== "paused") {
      return false;
    }

    let visibleIndex = 0;
    for (const item of snapshot.perks.inventory) {
      if (item.count <= 0) {
        continue;
      }

      const buttonY =
        layout.perkButtonStartY + visibleIndex * (layout.perkButtonSize + layout.perkButtonGap);
      visibleIndex += 1;
      if (
        pointInCenteredRect(
          x,
          y,
          layout.perkButtonX,
          buttonY,
          layout.perkButtonWidth,
          layout.perkButtonSize,
        )
      ) {
        return true;
      }
    }

    return false;
  }

  private handleTerminalButton(): void {
    const status = this.simulation.getSnapshot().status;
    if (this.viewportMode === "mobilePortrait") {
      return;
    }

    if (status === "paused") {
      this.resumeLevel("button");
    } else if (status === "won") {
      this.advanceLevel("button");
    } else if (status === "lost") {
      this.retryLevel("button");
    }
  }

  private handlePauseButton(): void {
    if (this.viewportMode === "mobilePortrait") {
      return;
    }

    this.togglePause("button");
  }

  private handleShopItem(perkId: PerkId): void {
    if (this.viewportMode === "mobilePortrait") {
      return;
    }

    if (this.simulation.purchasePerk(perkId)) {
      this.pendingFlipIndices = [];
      this.renderFrame();
    }
  }

  private handlePerkButton(perkId: PerkId): void {
    if (this.viewportMode === "mobilePortrait") {
      return;
    }

    if (this.simulation.activatePerk(perkId)) {
      this.pendingFlipIndices = [];
      this.renderFrame();
    }
  }

  private restartLevel(source: "keyboard"): void {
    tracePolarity("restart_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
    });
    this.simulation.restartLevel();
    this.resetTransientState();
    this.renderFrame();
  }

  private confirmProgress(source: "keyboard"): void {
    if (this.simulation.getSnapshot().status === "won") {
      this.advanceLevel(source);
      return;
    }

    if (this.simulation.getSnapshot().status === "lost") {
      this.retryLevel(source);
    }
  }

  private handleSpaceKey(event: KeyboardEvent): void {
    const status = this.simulation.getSnapshot().status;
    const canTogglePause =
      this.viewportMode !== "mobilePortrait" &&
      (status === "running" || status === "paused");
    const canConfirmTerminal = status === "won" || status === "lost";

    if (!canTogglePause && !canConfirmTerminal) {
      return;
    }

    event.preventDefault();
    if (event.repeat) {
      return;
    }

    if (canTogglePause) {
      this.togglePause("keyboard");
      return;
    }

    this.confirmProgress("keyboard");
  }

  private advanceLevel(source: "keyboard" | "button"): void {
    tracePolarity("next_level_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
    });
    if (!this.simulation.nextLevel()) {
      return;
    }
    this.resetTransientState();
    this.renderFrame();
  }

  private retryLevel(source: "keyboard" | "button"): void {
    tracePolarity("retry_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
    });
    if (this.simulation.getSnapshot().status !== "lost") {
      return;
    }
    this.simulation.restartLevel();
    this.resetTransientState();
    this.renderFrame();
  }

  private togglePause(source: "keyboard" | "button"): void {
    if (this.viewportMode === "mobilePortrait") {
      return;
    }

    const snapshot = this.simulation.getSnapshot();
    if (snapshot.status === "running") {
      this.pauseLevel(source);
      return;
    }

    if (snapshot.status === "paused") {
      this.resumeLevel(source);
    }
  }

  private pauseLevel(source: "keyboard" | "button"): void {
    tracePolarity("pause_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
    });

    if (!this.simulation.pauseLevel()) {
      return;
    }
    this.resetTransientState();
    this.renderFrame();
  }

  private resumeLevel(source: "keyboard" | "button"): void {
    tracePolarity("resume_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
    });

    if (!this.simulation.resumeLevel()) {
      return;
    }
    this.resetTransientState();
    this.renderFrame();
  }

  private resetTransientState(): void {
    this.accumulatorMs = 0;
    this.pendingFlipIndices = [];
  }

  private getAdminState(): PolarityAdminState {
    const snapshot = this.simulation.getSnapshot();
    return {
      level: snapshot.level,
      status: snapshot.status,
      availableActions: [
        "forcePassLevel",
        "skipLevel",
        "previousLevel",
        "jumpToLevel",
        "addScore",
      ],
    };
  }

  private forcePassLevel(): PolarityAdminResult {
    tracePolarity("admin_force_pass_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      statusBefore: this.simulation.getSnapshot().status,
    });

    const ok = this.simulation.forcePassLevel();
    this.accumulatorMs = 0;
    this.pendingFlipIndices = [];
    this.renderFrame();
    const snapshot = this.simulation.getSnapshot();

    return {
      ok,
      action: "forcePassLevel",
      message: ok ? "Current level forced into the won state." : "Level was already won.",
      snapshot,
    };
  }

  private skipLevel(source: "api" | "hotkey"): PolarityAdminResult {
    tracePolarity("admin_skip_level_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
      currentLevel: this.simulation.getSnapshot().level,
    });

    const ok = this.simulation.skipLevel();
    this.accumulatorMs = 0;
    this.pendingFlipIndices = [];
    this.renderFrame();
    const snapshot = this.simulation.getSnapshot();

    return {
      ok,
      action: "skipLevel",
      message: `Skipped to level ${snapshot.level}.`,
      snapshot,
    };
  }

  private previousLevel(source: "api" | "hotkey"): PolarityAdminResult {
    tracePolarity("admin_previous_level_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      source,
      currentLevel: this.simulation.getSnapshot().level,
    });

    const ok = this.simulation.previousLevel();
    this.accumulatorMs = 0;
    this.pendingFlipIndices = [];
    this.renderFrame();
    const snapshot = this.simulation.getSnapshot();

    return {
      ok,
      action: "previousLevel",
      message: ok
        ? "Moved to the previous level."
        : "No previous level exists before the current level.",
      snapshot,
    };
  }

  private jumpToLevel(level: number | string): PolarityAdminResult {
    tracePolarity("admin_jump_level_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      requestedLevel: level,
    });

    const ok = this.simulation.jumpToLevel(level);
    if (ok) {
      this.accumulatorMs = 0;
      this.pendingFlipIndices = [];
      this.renderFrame();
    }

    const snapshot = this.simulation.getSnapshot();
    return {
      ok,
      action: "jumpToLevel",
      message: ok
        ? `Jumped to level ${snapshot.level}.`
        : "Level must be an authored id like 1.1 or a valid generated whole-number level.",
      snapshot,
    };
  }

  private addScore(amount: number): PolarityAdminResult {
    tracePolarity("admin_add_score_requested", {
      updateBatch: this.updateBatchCount,
      pendingBefore: this.pendingFlipIndices.length,
      amount,
    });

    const ok = this.simulation.addScorePoints(amount);
    this.renderFrame();

    const snapshot = this.simulation.getSnapshot();
    return {
      ok,
      action: "addScore",
      message: ok
        ? `Added ${amount} score points.`
        : "Score amount must be a positive safe integer.",
      snapshot,
    };
  }

  private syncResponsiveLayout(): void {
    this.layout = this.computeLayout();
    this.pendingFlipIndices = [];
    this.cameras.main.setSize(this.layout.width, this.layout.height);

    this.applyLayoutToSceneObjects();
  }

  private computeLayout(): SceneLayout {
    const width = Math.max(320, Math.round(this.scale.gameSize.width || GAME_WIDTH));
    const height = Math.max(240, Math.round(this.scale.gameSize.height || GAME_HEIGHT));
    const safeArea = readSafeAreaInsets();
    const touchLayout = this.viewportMode !== "desktop";
    const compactHeight = height < 460;
    const compactWidth = width < 760;
    const outerMargin = touchLayout ? (compactHeight ? 8 : 12) : 24;
    const leftMargin = safeArea.left + outerMargin;
    const rightMargin = safeArea.right + outerMargin;
    const timerBarY = safeArea.top + outerMargin;
    const arenaY = timerBarY + TIMER_BAR_HEIGHT + (touchLayout ? (compactHeight ? 8 : 12) : 18);
    const bottomMargin = safeArea.bottom + outerMargin;
    const perkButtonSize = touchLayout ? (compactHeight || compactWidth ? 40 : 44) : 46;
    const perkButtonWidth = perkButtonSize * PERK_BUTTON_WIDTH_MULTIPLIER;
    const perkButtonGap = touchLayout || compactHeight ? 8 : 10;
    const perkRailGap = touchLayout ? 8 : 12;
    const perkRailWidth = this.viewportMode === "mobilePortrait" ? 0 : perkButtonWidth + perkRailGap;
    const availableArena = {
      x: leftMargin,
      y: arenaY,
      width: Math.max(240, width - leftMargin - rightMargin - perkRailWidth),
      height: Math.max(210, height - arenaY - bottomMargin),
    };
    const projection = createArenaProjection(ARENA_BOUNDS, availableArena);
    const arena = projection.screen;
    const pauseButtonSize = touchLayout ? (compactHeight || compactWidth ? 30 : 34) : 28;
    const pauseButtonHitSize = touchLayout ? 44 : 34;
    const pauseButtonX = arena.x + arena.width - pauseButtonSize / 2 - 4;
    const shopWidth = Math.min(
      touchLayout ? 220 : 282,
      Math.max(touchLayout ? 190 : 230, arena.width * (compactWidth ? 0.34 : 0.3)),
    );
    const shopRowHeight = touchLayout || compactHeight ? 42 : 50;
    const shopTitleHeight = touchLayout || compactHeight ? 28 : 34;
    const shopX = arena.x + (touchLayout ? 10 : 16);
    const shopRight = shopX + shopWidth;
    const centeredOverlayX = arena.x + arena.width / 2;
    const pauseOverlayX = compactWidth
      ? Math.min(
        arena.x + arena.width - TERMINAL_BUTTON_WIDTH / 2 - 16,
        Math.max(centeredOverlayX, shopRight + TERMINAL_BUTTON_WIDTH / 2 + 12),
      )
      : centeredOverlayX;
    const bannerOffset = compactHeight ? 16 : 24;
    const bannerGap = compactHeight ? 32 : 46;
    const terminalButtonY = arena.y + arena.height / 2 + (compactHeight ? 54 : 76);

    return {
      width,
      height,
      availableArena,
      arena,
      projection,
      safeArea,
      scoreTextX: arena.x + 12,
      scoreTextY: timerBarY + TIMER_BAR_HEIGHT / 2,
      levelTextX: arena.x + arena.width / 2,
      levelTextY: timerBarY + TIMER_BAR_HEIGHT / 2,
      timerTextX: pauseButtonX - pauseButtonSize / 2 - 8,
      timerTextY: timerBarY + TIMER_BAR_HEIGHT / 2,
      pauseButtonX,
      pauseButtonY: timerBarY + TIMER_BAR_HEIGHT / 2,
      pauseButtonSize,
      pauseButtonHitSize,
      shopX,
      shopY: arena.y + (touchLayout ? 10 : 16),
      shopWidth,
      shopRowHeight,
      shopTitleHeight,
      shopFontSize: touchLayout || compactHeight ? "12px" : "14px",
      shopTitleFontSize: touchLayout || compactHeight ? "14px" : "16px",
      perkButtonX: width - rightMargin - perkButtonWidth / 2,
      perkButtonStartY: arena.y + (touchLayout || compactHeight ? 58 : 72),
      perkButtonWidth,
      perkButtonSize,
      perkButtonGap,
      perkButtonFontSize: touchLayout || compactHeight ? "14px" : "15px",
      perkButtonCountFontSize: touchLayout || compactHeight ? "11px" : "12px",
      statusTextX: arena.x + arena.width / 2,
      statusTextY: timerBarY + TIMER_BAR_HEIGHT + 4,
      hintTextX: arena.x + arena.width / 2,
      hintTextY: height - safeArea.bottom - (compactHeight ? 18 : 28),
      bannerTextX: arena.x + arena.width / 2,
      pauseOverlayX,
      bannerTextY: arena.y + arena.height / 2 - bannerOffset,
      bannerSubTextY: arena.y + arena.height / 2 + bannerGap / 2,
      timerBarY,
      timerBarHeight: TIMER_BAR_HEIGHT,
      terminalButtonX: arena.x + arena.width / 2,
      terminalButtonY: Math.min(terminalButtonY, arena.y + arena.height - TERMINAL_BUTTON_HEIGHT / 2 - 16),
      fontSizes: {
        hud: touchLayout ? (compactHeight || compactWidth ? "15px" : "18px") : "20px",
        status: "20px",
        hint: compactWidth ? "16px" : "18px",
        banner: touchLayout ? (compactHeight ? "30px" : "36px") : "42px",
        bannerSub: touchLayout || compactWidth ? "16px" : "20px",
      },
    };
  }

  private applyLayoutToSceneObjects(): void {
    if (
      !this.scoreText ||
      !this.levelText ||
      !this.timerText ||
      !this.statusText ||
      !this.hintText
    ) {
      return;
    }

    const layout = this.layout;
    this.scoreText.setPosition(layout.scoreTextX, layout.scoreTextY);
    this.scoreText.setFontSize(layout.fontSizes.hud);
    this.levelText.setPosition(layout.levelTextX, layout.levelTextY);
    this.levelText.setFontSize(layout.fontSizes.hud);
    this.timerText.setPosition(layout.timerTextX, layout.timerTextY);
    this.timerText.setFontSize(layout.fontSizes.hud);
    this.statusText.setPosition(layout.statusTextX, layout.statusTextY);
    this.statusText.setFontSize(layout.fontSizes.status);
    this.hintText.setPosition(layout.hintTextX, layout.hintTextY);
    this.hintText.setFontSize(layout.fontSizes.hint);

    this.bannerText.setPosition(layout.bannerTextX, layout.bannerTextY);
    this.bannerText.setFontSize(layout.fontSizes.banner);
    this.bannerText.setWordWrapWidth(layout.arena.width * 0.86, true);
    this.bannerSubText.setPosition(layout.bannerTextX, layout.bannerSubTextY);
    this.bannerSubText.setFontSize(layout.fontSizes.bannerSub);
    this.bannerSubText.setWordWrapWidth(layout.arena.width * 0.86, true);

    this.terminalButtonText.setPosition(layout.terminalButtonX, layout.terminalButtonY);
    this.terminalButtonZone.setPosition(layout.terminalButtonX, layout.terminalButtonY);
    this.terminalButtonZone.setSize(TERMINAL_BUTTON_WIDTH, TERMINAL_BUTTON_HEIGHT);
    this.pauseButtonZone.setPosition(layout.pauseButtonX, layout.pauseButtonY);
    this.pauseButtonZone.setSize(layout.pauseButtonHitSize, layout.pauseButtonHitSize);

    this.shopTitleText.setFontSize(layout.shopTitleFontSize);
    for (const itemText of this.shopItemTexts) {
      itemText.setFontSize(layout.shopFontSize);
      itemText.setWordWrapWidth(layout.shopWidth - 24, true);
    }
    for (const buttonText of this.perkButtonTexts) {
      buttonText.setFontSize(layout.perkButtonFontSize);
    }
    for (const countText of this.perkButtonCountTexts) {
      countText.setFontSize(layout.perkButtonCountFontSize);
    }
  }

  private renderFrame(): void {
    const snapshot = this.simulation.getSnapshot();
    this.drawBoard(snapshot);
    this.syncPauseButton(snapshot);
    this.syncTerminalButton(snapshot);
    this.syncPerkShop(snapshot);
    this.syncPerkButtons(snapshot);

    this.scoreText.setText(`score ${snapshot.score.totalPoints}`);
    this.levelText.setText(`level ${snapshot.level}`);
    this.timerText.setText(`${Math.ceil(snapshot.timerRemainingMs / 1000)}s`);
    this.statusText.setText(
      `L ${snapshot.walls.left.color}  R ${snapshot.walls.right.color}  T ${snapshot.walls.top.color}  B ${snapshot.walls.bottom.color}`,
    );
    this.hintText.setText(
      snapshot.status === "running"
        ? this.adminEnabled
          ? "Click to flip. Ctrl+S skip. Ctrl+A prev."
          : "Click to flip charge."
        : "",
    );
    const showTopHud = this.viewportMode !== "mobilePortrait";
    this.scoreText.setVisible(showTopHud);
    this.levelText.setVisible(showTopHud);
    this.timerText.setVisible(showTopHud);
    this.statusText.setVisible(false);
    this.hintText.setVisible(false);

    const bannerX = snapshot.status === "paused" ? this.layout.pauseOverlayX : this.layout.bannerTextX;
    this.bannerText.setX(bannerX);
    this.bannerSubText.setX(bannerX);

    if (snapshot.status === "won") {
      this.bannerText.setColor(COLORS.success);
      this.bannerText.setText("Level Cleared");
      this.bannerSubText.setText("");
    } else if (snapshot.status === "lost") {
      this.bannerText.setColor(COLORS.failure);
      this.bannerText.setText("Collision");
      this.bannerSubText.setText("");
    } else if (snapshot.status === "paused") {
      this.bannerText.setColor(COLORS.textPrimary);
      this.bannerText.setText("Paused");
      this.bannerSubText.setText("");
    } else {
      this.bannerText.setText("");
      this.bannerSubText.setText("");
    }
  }

  private syncPauseButton(snapshot: SimulationSnapshot): void {
    const showButton =
      this.viewportMode !== "mobilePortrait" &&
      (snapshot.status === "running" || snapshot.status === "paused");

    this.pauseButtonZone.setVisible(showButton);
    this.pauseButtonZone.input!.enabled = showButton;
  }

  private syncTerminalButton(snapshot: SimulationSnapshot): void {
    this.terminalButtonBackground.clear();
    const showButton =
      this.viewportMode !== "mobilePortrait" &&
      (snapshot.status === "paused" || snapshot.status === "won" || snapshot.status === "lost");

    this.terminalButtonText.setVisible(showButton);
    this.terminalButtonZone.setVisible(showButton);
    this.terminalButtonZone.input!.enabled = showButton;

    if (!showButton) {
      this.terminalButtonText.setText("");
      return;
    }

    const label =
      snapshot.status === "paused" ? "Resume" : snapshot.status === "won" ? "Next" : "Retry";
    const layout = this.layout;
    const buttonX = snapshot.status === "paused" ? layout.pauseOverlayX : layout.terminalButtonX;
    const x = buttonX - TERMINAL_BUTTON_WIDTH / 2;
    const y = layout.terminalButtonY - TERMINAL_BUTTON_HEIGHT / 2;

    this.terminalButtonText.setPosition(buttonX, layout.terminalButtonY);
    this.terminalButtonZone.setPosition(buttonX, layout.terminalButtonY);
    this.terminalButtonText.setText(label);
    this.terminalButtonBackground.fillStyle(0x091c29, 0.94);
    this.terminalButtonBackground.fillRoundedRect(
      x,
      y,
      TERMINAL_BUTTON_WIDTH,
      TERMINAL_BUTTON_HEIGHT,
      8,
    );
    this.terminalButtonBackground.lineStyle(1, 0xe9f7ff, 0.28);
    this.terminalButtonBackground.strokeRoundedRect(
      x,
      y,
      TERMINAL_BUTTON_WIDTH,
      TERMINAL_BUTTON_HEIGHT,
      8,
    );
  }

  private syncPerkShop(snapshot: SimulationSnapshot): void {
    const showShop = this.shouldShowPerkShop(snapshot);
    const layout = this.layout;
    this.shopTitleText.setVisible(showShop);
    this.shopTitleText.setPosition(layout.shopX + 12, layout.shopY + layout.shopTitleHeight / 2);

    snapshot.perks.shop.forEach((item, index) => {
      const itemText = this.shopItemTexts[index];
      const itemZone = this.shopItemZones[index];
      const rowY =
        layout.shopY +
        layout.shopTitleHeight +
        index * layout.shopRowHeight +
        layout.shopRowHeight / 2;

      itemText.setVisible(showShop);
      itemText.setPosition(layout.shopX + 12, rowY);
      itemText.setText(`${item.sign} ${item.name} ${item.cost} x${item.ownedCount}`);
      itemText.setColor(item.affordable ? COLORS.textPrimary : COLORS.textMuted);

      itemZone.setVisible(showShop);
      itemZone.setPosition(layout.shopX + layout.shopWidth / 2, rowY);
      itemZone.setSize(layout.shopWidth, layout.shopRowHeight);
      itemZone.input!.enabled = showShop && item.canPurchase;
    });
  }

  private syncPerkButtons(snapshot: SimulationSnapshot): void {
    const showRail =
      this.viewportMode !== "mobilePortrait" &&
      (snapshot.status === "running" || snapshot.status === "paused");
    const layout = this.layout;
    let visibleIndex = 0;

    snapshot.perks.inventory.forEach((item, index) => {
      const showButton = showRail && item.count > 0;
      const buttonText = this.perkButtonTexts[index];
      const countText = this.perkButtonCountTexts[index];
      const buttonZone = this.perkButtonZones[index];

      if (!showButton) {
        buttonText.setVisible(false);
        countText.setVisible(false);
        buttonZone.setVisible(false);
        buttonZone.input!.enabled = false;
        return;
      }

      const y = layout.perkButtonStartY + visibleIndex * (layout.perkButtonSize + layout.perkButtonGap);
      visibleIndex += 1;

      buttonText.setVisible(true);
      buttonText.setPosition(layout.perkButtonX, y - 2);
      buttonText.setText(item.sign);
      buttonText.setColor(item.canActivate ? COLORS.textPrimary : COLORS.textMuted);

      countText.setVisible(true);
      countText.setPosition(
        layout.perkButtonX + layout.perkButtonWidth / 2 - layout.perkButtonSize * 0.22,
        y + layout.perkButtonSize * 0.29,
      );
      countText.setText(String(item.count));
      countText.setColor(item.canActivate ? COLORS.textPrimary : COLORS.textMuted);

      buttonZone.setVisible(true);
      buttonZone.setPosition(layout.perkButtonX, y);
      buttonZone.setSize(layout.perkButtonWidth, layout.perkButtonSize);
      buttonZone.input!.enabled = item.canActivate;
    });
  }

  private drawBoard(snapshot: SimulationSnapshot): void {
    const graphics = this.graphics;
    const layout = this.layout;
    const arena = layout.arena;
    graphics.clear();

    graphics.fillGradientStyle(
      COLORS.backgroundTop,
      COLORS.backgroundTop,
      COLORS.backgroundBottom,
      COLORS.backgroundBottom,
      1,
    );
    graphics.fillRect(0, 0, layout.width, layout.height);

    graphics.fillStyle(COLORS.backgroundGlow, 0.34);
    graphics.fillCircle(arena.x + 76, arena.y + 42, Math.min(arena.width, arena.height) * 0.22);
    graphics.fillCircle(
      arena.x + arena.width - 82,
      arena.y + arena.height - 58,
      Math.min(arena.width, arena.height) * 0.26,
    );

    const topWallColor = wallColorToFillColor(snapshot.walls.top.color);
    const bottomWallColor = wallColorToFillColor(snapshot.walls.bottom.color);
    const leftWallColor = wallColorToFillColor(snapshot.walls.left.color);
    const rightWallColor = wallColorToFillColor(snapshot.walls.right.color);

    graphics.fillStyle(COLORS.arenaFill, 1);
    graphics.fillRoundedRect(arena.x, arena.y, arena.width, arena.height, 18);
    graphics.lineStyle(10, topWallColor, 1);
    graphics.lineBetween(arena.x + 18, arena.y, arena.x + arena.width - 18, arena.y);
    graphics.lineStyle(10, bottomWallColor, 1);
    graphics.lineBetween(
      arena.x + 18,
      arena.y + arena.height,
      arena.x + arena.width - 18,
      arena.y + arena.height,
    );
    graphics.lineStyle(10, leftWallColor, 1);
    graphics.lineBetween(arena.x, arena.y + 18, arena.x, arena.y + arena.height - 18);
    graphics.lineStyle(10, rightWallColor, 1);
    graphics.lineBetween(
      arena.x + arena.width,
      arena.y + 18,
      arena.x + arena.width,
      arena.y + arena.height - 18,
    );
    graphics.lineStyle(2, COLORS.arenaEdge, 0.95);
    graphics.strokeRoundedRect(arena.x, arena.y, arena.width, arena.height, 18);
    graphics.lineStyle(1, COLORS.arenaInnerEdge, 0.9);
    graphics.strokeRoundedRect(
      arena.x + 8,
      arena.y + 8,
      arena.width - 16,
      arena.height - 16,
      14,
    );

    graphics.lineStyle(1, COLORS.arenaInnerEdge, 0.25);
    const gridStep = Math.max(44, Math.min(64, Math.min(arena.width, arena.height) / 10));
    for (let x = arena.x + gridStep; x < arena.x + arena.width; x += gridStep) {
      graphics.lineBetween(x, arena.y + 12, x, arena.y + arena.height - 12);
    }
    for (let y = arena.y + gridStep; y < arena.y + arena.height; y += gridStep) {
      graphics.lineBetween(arena.x + 12, y, arena.x + arena.width - 12, y);
    }

    const timerProgress = Phaser.Math.Clamp(snapshot.timerRemainingMs / snapshot.levelDurationMs, 0, 1);
    graphics.fillStyle(0x203446, 1);
    graphics.fillRoundedRect(arena.x, layout.timerBarY, arena.width, layout.timerBarHeight, 8);
    graphics.fillStyle(0x8de5ff, 1);
    graphics.fillRoundedRect(
      arena.x,
      layout.timerBarY,
      arena.width * timerProgress,
      layout.timerBarHeight,
      8,
    );

    for (const ball of snapshot.balls) {
      this.drawBall(ball);
    }

    if (snapshot.status !== "running") {
      graphics.fillStyle(0x02101a, 0.48);
      graphics.fillRect(arena.x, arena.y, arena.width, arena.height);
    }

    this.drawPerkShop(snapshot);
    this.drawPerkButtons(snapshot);
    this.drawPauseButton(snapshot);
  }

  private drawPerkShop(snapshot: SimulationSnapshot): void {
    if (!this.shouldShowPerkShop(snapshot)) {
      return;
    }

    const graphics = this.graphics;
    const layout = this.layout;
    const panelHeight = layout.shopTitleHeight + snapshot.perks.shop.length * layout.shopRowHeight;
    graphics.fillStyle(0x071923, SHOP_PANEL_ALPHA);
    graphics.fillRoundedRect(layout.shopX, layout.shopY, layout.shopWidth, panelHeight, 8);
    graphics.lineStyle(1, 0xe9f7ff, 0.22);
    graphics.strokeRoundedRect(layout.shopX, layout.shopY, layout.shopWidth, panelHeight, 8);

    snapshot.perks.shop.forEach((item, index) => {
      const rowY = layout.shopY + layout.shopTitleHeight + index * layout.shopRowHeight;
      graphics.fillStyle(item.canPurchase ? 0x12344a : 0x0b202c, item.affordable ? 0.9 : 0.72);
      graphics.fillRoundedRect(
        layout.shopX + 6,
        rowY + 4,
        layout.shopWidth - 12,
        layout.shopRowHeight - 8,
        6,
      );
      graphics.lineStyle(1, item.canPurchase ? 0x8de5ff : 0xe9f7ff, item.canPurchase ? 0.32 : 0.12);
      graphics.strokeRoundedRect(
        layout.shopX + 6,
        rowY + 4,
        layout.shopWidth - 12,
        layout.shopRowHeight - 8,
        6,
      );
    });
  }

  private drawPerkButtons(snapshot: SimulationSnapshot): void {
    if (
      this.viewportMode === "mobilePortrait" ||
      (snapshot.status !== "running" && snapshot.status !== "paused")
    ) {
      return;
    }

    const graphics = this.graphics;
    const layout = this.layout;
    let visibleIndex = 0;
    snapshot.perks.inventory.forEach((item) => {
      if (item.count <= 0) {
        return;
      }

      const y = layout.perkButtonStartY + visibleIndex * (layout.perkButtonSize + layout.perkButtonGap);
      visibleIndex += 1;
      const x = layout.perkButtonX - layout.perkButtonWidth / 2;
      const top = y - layout.perkButtonSize / 2;
      const active =
        (item.id === "freeze_balls" &&
          snapshot.perks.activeEffects.freezeBallsRemainingMs > 0) ||
        (item.id === "neutralize_walls" && snapshot.perks.activeEffects.wallsNeutralized);

      graphics.fillStyle(item.canActivate ? 0x091c29 : 0x111923, 0.94);
      graphics.fillRoundedRect(x, top, layout.perkButtonWidth, layout.perkButtonSize, 8);
      graphics.lineStyle(
        active ? 2 : 1,
        active ? 0x8de5ff : 0xe9f7ff,
        active ? 0.86 : item.canActivate ? 0.3 : 0.16,
      );
      graphics.strokeRoundedRect(x, top, layout.perkButtonWidth, layout.perkButtonSize, 8);
    });
  }

  private drawPauseButton(snapshot: SimulationSnapshot): void {
    if (
      this.viewportMode === "mobilePortrait" ||
      (snapshot.status !== "running" && snapshot.status !== "paused")
    ) {
      return;
    }

    const graphics = this.graphics;
    const layout = this.layout;
    const size = layout.pauseButtonSize;
    const x = layout.pauseButtonX - size / 2;
    const y = layout.pauseButtonY - size / 2;
    const radius = snapshot.status === "paused" ? 8 : 7;

    graphics.fillStyle(snapshot.status === "paused" ? 0x12344a : 0x091c29, 0.94);
    graphics.fillRoundedRect(x, y, size, size, radius);
    graphics.lineStyle(1, 0xe9f7ff, snapshot.status === "paused" ? 0.42 : 0.28);
    graphics.strokeRoundedRect(x, y, size, size, radius);

    graphics.fillStyle(PAUSE_BUTTON_ICON_COLOR, 1);
    if (snapshot.status === "paused") {
      graphics.fillTriangle(
        layout.pauseButtonX - size * 0.16,
        layout.pauseButtonY - size * 0.25,
        layout.pauseButtonX - size * 0.16,
        layout.pauseButtonY + size * 0.25,
        layout.pauseButtonX + size * 0.22,
        layout.pauseButtonY,
      );
      return;
    }

    const barWidth = Math.max(3, Math.round(size * 0.14));
    const barHeight = Math.round(size * 0.46);
    const gap = Math.round(size * 0.14);
    const barY = layout.pauseButtonY - barHeight / 2;
    graphics.fillRoundedRect(
      layout.pauseButtonX - gap / 2 - barWidth,
      barY,
      barWidth,
      barHeight,
      2,
    );
    graphics.fillRoundedRect(
      layout.pauseButtonX + gap / 2,
      barY,
      barWidth,
      barHeight,
      2,
    );
  }

  private drawBall(ball: BallSnapshot): void {
    const graphics = this.graphics;
    const projection = this.layout.projection;
    const position = worldToScreenPoint(projection, { x: ball.x, y: ball.y });
    const radius = worldLengthToScreen(projection, ball.radius);
    const velocityX = ball.vx * projection.scale;
    const velocityY = ball.vy * projection.scale;

    graphics.lineStyle(5, chargeToHighlightColor(ball.charge), 0.22);
    graphics.lineBetween(
      position.x,
      position.y,
      position.x - velocityX * 0.18,
      position.y - velocityY * 0.18,
    );

    graphics.fillStyle(chargeToFillColor(ball.charge), 1);
    graphics.fillCircle(position.x, position.y, radius);
    graphics.lineStyle(3, chargeToHighlightColor(ball.charge), 1);
    graphics.strokeCircle(position.x, position.y, radius);

    graphics.lineStyle(3, 0xf7fbff, 1);
    graphics.lineBetween(position.x - 7, position.y, position.x + 7, position.y);
    if (ball.charge > 0) {
      graphics.lineBetween(position.x, position.y - 7, position.x, position.y + 7);
    }
  }
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function pointInCenteredRect(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  width: number,
  height: number,
): boolean {
  return pointInRect(x, y, centerX - width / 2, centerY - height / 2, width, height);
}

function pointInRect(
  x: number,
  y: number,
  rectX: number,
  rectY: number,
  width: number,
  height: number,
): boolean {
  return x >= rectX && x <= rectX + width && y >= rectY && y <= rectY + height;
}

function readSafeAreaInsets(): SafeAreaInsets {
  const styles = getComputedStyle(document.documentElement);
  return {
    top: parseCssPixels(styles.getPropertyValue("--safe-top")),
    right: parseCssPixels(styles.getPropertyValue("--safe-right")),
    bottom: parseCssPixels(styles.getPropertyValue("--safe-bottom")),
    left: parseCssPixels(styles.getPropertyValue("--safe-left")),
  };
}

function parseCssPixels(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
